"use client";

import Input, { type InputProps } from "@/components/ui/input";
import { useCurrentLocale } from "@/hooks/useCurrentLocale";
import { useScopedI18n } from "@/messages/client";
import { sendCaptcha } from "@/server/form";
import { useDebounceFn } from "ahooks";
import { useEffect, useState } from "react";
import { v4 as uuid } from "uuid";
import useSWRMutation from "swr/mutation";
import Loading from "@/components/ui/loading";
import Image from "next/image";
import toast from "react-hot-toast";
import { cn } from "@/lib/utils";

export default function FormCaptcha(
  props: InputProps & {
    email?: string;
  }
) {
  const t = useScopedI18n("找回密码");

  const locale = useCurrentLocale();

  const [identifier, setIdentifier] = useState(uuid());

  const { data, isMutating, trigger } = useSWRMutation(
    () =>
      props.email
        ? [`/customers/captcha`, props.email, identifier]
        : null,
    async([, email]) => {
      const res = await sendCaptcha({
        email: email,
        language: locale === "cn" ? "zh" : locale,
      });

      if ("errors" in res && (res as any).errors?.[0]?.message) {
        toast.error((res as any).errors?.[0]?.message);
      }

      return res;
    }
  );

  const { run } = useDebounceFn(
    () => {
      setIdentifier(uuid());
    },
    {
      wait: 300,
    }
  );

  useEffect(() => {
    if (props.email && identifier) {
      trigger();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identifier, props.email]);

  if (!props.email) {
    return null;
  }

  return (
    <div className={cn(
      "mt-5 flex items-center space-x-5 md:space-x-8 lg:space-x-12",
      props.className,
    )}>
      <div className="w-full md:w-[320px]">
        <Input
          {...props}
          placeholder={t("请输入右侧图形验证码")}
        />
      </div>

      <Loading loading={isMutating} hideText>
        <div
          className="relative h-7 w-20 flex-shrink-0 cursor-pointer bg-slate-200 md:h-10 md:w-[7.5rem]"
          onClick={run}>
          {data?.image && (
            <Image src={data.image} alt="Code" fill className="object-cover" />
          )}
        </div>
      </Loading>
    </div>
  );
}